import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import {
  CaretLeft,
  CaretRight,
  CalendarBlank,
  CreditCard,
  Repeat,
  Clock,
  ClipboardText,
  ArrowsClockwise,
  GoogleLogo,
  CheckCircle,
  Warning,
  Export,
} from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';

const API_URL = (typeof window !== 'undefined' && window.location.hostname && !/^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname)) ? window.location.origin : process.env.REACT_APP_BACKEND_URL;

const currencySymbols = {
  USD: 'US$', AUD: 'A$', SGD: 'S$', INR: '₹', EUR: '€', GBP: '£',
  NZD: 'NZ$', CAD: 'C$', JPY: '¥', CNY: '¥', KRW: '₩', MYR: 'RM',
};
const formatAmount = (amount, currency) => {
  if (amount == null) return '';
  const symbol = currencySymbols[currency] || (currency ? currency + ' ' : '$');
  return `${symbol}${amount.toFixed(2)}`;
};

const categoryColors = {
  bill: { bg: 'bg-[#fde8e8]', dot: 'bg-[#9d1f2d]', text: 'text-[#9d1f2d]' },
  subscription: { bg: 'bg-[#ede9fe]', dot: 'bg-[#5b21b6]', text: 'text-[#5b21b6]' },
  appointment: { bg: 'bg-[#dbeafe]', dot: 'bg-[#005bc1]', text: 'text-[#005bc1]' },
  task: { bg: 'bg-[#fef3c7]', dot: 'bg-[#b45309]', text: 'text-[#b45309]' },
  general: { bg: 'bg-[#f2f4f6]', dot: 'bg-[#596065]', text: 'text-[#596065]' },
};

const getCategoryIcon = (category, size = 16) => {
  switch (category) {
    case 'bill': return <CreditCard size={size} weight="duotone" className="text-[#9d1f2d]" />;
    case 'subscription': return <Repeat size={size} weight="duotone" className="text-[#5b21b6]" />;
    case 'appointment': return <CalendarBlank size={size} weight="duotone" className="text-[#005bc1]" />;
    case 'task': return <ClipboardText size={size} weight="duotone" className="text-[#b45309]" />;
    default: return <Clock size={size} weight="duotone" className="text-[#596065]" />;
  }
};

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function CalendarPage() {
  const [tasks, setTasks] = useState([]);
  const [gcalEvents, setGcalEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [checkingCal, setCheckingCal] = useState(true);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  useEffect(() => {
    fetchTasks();
    checkCalendarStatus();
  }, []);

  useEffect(() => {
    if (calendarConnected) fetchGcalEvents();
  }, [calendarConnected, month, year]);

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/tasks`, { withCredentials: true });
      setTasks(response.data);
    } catch (error) {
      console.error('Fetch tasks error:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkCalendarStatus = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/calendar/status`, { withCredentials: true });
      setCalendarConnected(res.data.connected);
    } catch {
      setCalendarConnected(false);
    } finally {
      setCheckingCal(false);
    }
  };

  const fetchGcalEvents = async () => {
    try {
      const start = new Date(year, month, 1).toISOString();
      const end = new Date(year, month + 1, 0, 23, 59, 59).toISOString();
      const res = await axios.get(`${API_URL}/api/calendar/events?start=${start}&end=${end}`, { withCredentials: true });
      setGcalEvents(res.data.events || []);
    } catch (err) {
      console.error('Fetch gcal events error:', err);
    }
  };

  const handleSyncAll = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API_URL}/api/calendar/sync`, {}, { withCredentials: true });
      toast.success(`Synced ${res.data.synced} tasks to Google Calendar`);
      if (res.data.errors > 0) {
        toast.warning(`${res.data.errors} tasks failed to sync`);
      }
      fetchGcalEvents();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Sync failed';
      if (detail.includes('scope')) {
        toast.error('Calendar permission needed. Please reconnect Google from Connections page.');
      } else {
        toast.error(detail);
      }
    } finally {
      setSyncing(false);
    }
  };

  // Build calendar grid
  const calendarDays = useMemo(() => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    let startDow = firstDay.getDay();
    startDow = startDow === 0 ? 6 : startDow - 1; // Monday-start

    const days = [];
    // Prev month padding
    const prevLast = new Date(year, month, 0).getDate();
    for (let i = startDow - 1; i >= 0; i--) {
      days.push({ date: new Date(year, month - 1, prevLast - i), outside: true });
    }
    // Current month
    for (let d = 1; d <= lastDay.getDate(); d++) {
      days.push({ date: new Date(year, month, d), outside: false });
    }
    // Next month padding
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({ date: new Date(year, month + 1, i), outside: true });
    }
    return days;
  }, [year, month]);

  // Map tasks to dates
  const tasksByDate = useMemo(() => {
    const map = {};
    tasks.forEach(task => {
      if (!task.due_date) return;
      const d = new Date(task.due_date);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!map[key]) map[key] = [];
      map[key].push(task);
    });
    return map;
  }, [tasks]);

  // Map gcal events to dates
  const gcalByDate = useMemo(() => {
    const map = {};
    gcalEvents.forEach(ev => {
      if (!ev.start) return;
      const d = new Date(ev.start);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!map[key]) map[key] = [];
      map[key].push(ev);
    });
    return map;
  }, [gcalEvents]);

  const getDateKey = (date) => `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;

  const today = new Date();
  const todayKey = getDateKey(today);

  const selectedKey = selectedDate ? getDateKey(selectedDate) : null;
  const selectedTasks = selectedKey ? (tasksByDate[selectedKey] || []) : [];
  const selectedGcalEvents = selectedKey ? (gcalByDate[selectedKey] || []) : [];

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const goToday = () => { setCurrentDate(new Date()); setSelectedDate(new Date()); };

  const monthLabel = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  return (
    <Layout>
      <Toaster position="top-right" richColors />
      <div className="p-4 lg:p-10">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="display-lg text-4xl" data-testid="calendar-title">Calendar</h1>
            <p className="body-lg mt-2">Visualize your schedule at a glance.</p>
          </div>
          <div className="flex items-center gap-3">
            {!checkingCal && calendarConnected && (
              <button
                onClick={handleSyncAll}
                disabled={syncing}
                className="btn-secondary flex items-center gap-2 text-sm"
                data-testid="sync-gcal-btn"
              >
                <Export size={18} weight="bold" />
                {syncing ? 'Syncing...' : 'Sync to Google Calendar'}
              </button>
            )}
            {!checkingCal && !calendarConnected && (
              <a
                href="/connections"
                className="btn-secondary flex items-center gap-2 text-sm"
                data-testid="connect-gcal-btn"
              >
                <GoogleLogo size={18} weight="bold" />
                Connect Google Calendar
              </a>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-4 gap-6">
          {/* Calendar Grid */}
          <div className="lg:col-span-3">
            <div className="surface-card p-4 sm:p-6">
              {/* Month Navigation */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <button onClick={prevMonth} className="p-2 hover:bg-[#f2f4f6] rounded-xl transition-colors" data-testid="prev-month">
                    <CaretLeft size={20} weight="bold" className="text-[#596065]" />
                  </button>
                  <h2 className="title-md text-xl min-w-[200px] text-center" data-testid="month-label">{monthLabel}</h2>
                  <button onClick={nextMonth} className="p-2 hover:bg-[#f2f4f6] rounded-xl transition-colors" data-testid="next-month">
                    <CaretRight size={20} weight="bold" className="text-[#596065]" />
                  </button>
                </div>
                <button onClick={goToday} className="btn-tertiary text-sm px-4 py-2" data-testid="go-today">
                  Today
                </button>
              </div>

              {/* Day Headers */}
              <div className="grid grid-cols-7 mb-2">
                {DAYS.map(d => (
                  <div key={d} className="text-center label-md text-xs py-2 text-[#596065]">{d}</div>
                ))}
              </div>

              {/* Calendar Grid */}
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="animate-spin w-8 h-8 border-4 border-[#ebeef2] border-t-[#005bc1] rounded-full"></div>
                </div>
              ) : (
                <div className="grid grid-cols-7 border-t border-l border-[#ebeef2]">
                  {calendarDays.map((day, idx) => {
                    const key = getDateKey(day.date);
                    const dayTasks = tasksByDate[key] || [];
                    const isToday = key === todayKey;
                    const isSelected = selectedKey === key;
                    const hasItems = dayTasks.length > 0;

                    return (
                      <div
                        key={idx}
                        onClick={() => setSelectedDate(day.date)}
                        className={`
                          min-h-[90px] sm:min-h-[110px] border-r border-b border-[#ebeef2] p-1.5 sm:p-2 cursor-pointer transition-colors
                          ${day.outside ? 'bg-[#fafafa]' : 'bg-white hover:bg-[#f9f9fb]'}
                          ${isSelected ? 'ring-2 ring-[#005bc1] ring-inset bg-[#f0f7ff]' : ''}
                        `}
                        data-testid={`cal-day-${day.date.getDate()}-${day.outside ? 'outside' : 'current'}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className={`
                            text-xs sm:text-sm font-medium inline-flex items-center justify-center
                            ${day.outside ? 'text-[#c0c5c9]' : 'text-[#2d3338]'}
                            ${isToday ? 'w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-[#005bc1] text-white' : ''}
                          `}>
                            {day.date.getDate()}
                          </span>
                          {hasItems && !day.outside && (
                            <span className="text-[10px] text-[#596065] font-medium">{dayTasks.length}</span>
                          )}
                        </div>
                        {!day.outside && (
                          <div className="space-y-0.5">
                            {dayTasks.slice(0, 3).map(task => {
                              const colors = categoryColors[task.category] || categoryColors.general;
                              return (
                                <div
                                  key={task.task_id}
                                  className={`${colors.bg} rounded px-1.5 py-0.5 truncate hidden sm:block`}
                                  title={task.title}
                                >
                                  <span className={`text-[10px] font-medium ${colors.text} truncate block`}>
                                    {task.title}
                                  </span>
                                </div>
                              );
                            })}
                            {dayTasks.length > 3 && (
                              <span className="text-[10px] text-[#596065] pl-1">+{dayTasks.length - 3} more</span>
                            )}
                            {/* Mobile: show dots only */}
                            {dayTasks.length > 0 && (
                              <div className="flex gap-0.5 sm:hidden">
                                {dayTasks.slice(0, 4).map(task => {
                                  const colors = categoryColors[task.category] || categoryColors.general;
                                  return <div key={task.task_id} className={`w-1.5 h-1.5 rounded-full ${colors.dot}`}></div>;
                                })}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Legend */}
              <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-[#ebeef2]">
                {Object.entries(categoryColors).map(([cat, colors]) => (
                  <div key={cat} className="flex items-center gap-1.5">
                    <div className={`w-2.5 h-2.5 rounded-full ${colors.dot}`}></div>
                    <span className="text-xs text-[#596065] capitalize">{cat === 'general' ? 'Other' : cat + 's'}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar - Selected Day Detail */}
          <div className="lg:col-span-1 space-y-4">
            {selectedDate ? (
              <>
                <div className="surface-card p-5">
                  <p className="label-md text-xs text-[#596065] mb-1">
                    {selectedDate.toLocaleDateString('default', { weekday: 'long' })}
                  </p>
                  <p className="text-2xl font-bold text-[#2d3338]" data-testid="selected-date-label">
                    {selectedDate.toLocaleDateString('default', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </p>
                  <p className="text-sm text-[#596065] mt-1">
                    {selectedTasks.length} task{selectedTasks.length !== 1 ? 's' : ''}
                    {selectedGcalEvents.length > 0 && ` · ${selectedGcalEvents.length} calendar event${selectedGcalEvents.length !== 1 ? 's' : ''}`}
                  </p>
                </div>

                {/* Tasks for selected day */}
                {selectedTasks.length > 0 ? (
                  <div className="space-y-2">
                    {selectedTasks.map(task => {
                      const colors = categoryColors[task.category] || categoryColors.general;
                      const isCompleted = task.status === 'completed';
                      return (
                        <div
                          key={task.task_id}
                          className={`surface-card p-4 border-l-4 ${isCompleted ? 'border-[#0f6b3a] opacity-60' : `border-l-4`}`}
                          style={{ borderLeftColor: isCompleted ? '#0f6b3a' : undefined }}
                          data-testid={`sidebar-task-${task.task_id}`}
                        >
                          <div className={`border-l-0 ${!isCompleted ? colors.bg : ''} -m-4 p-4 rounded-r-xl`}>
                            <div className="flex items-start gap-2">
                              {isCompleted ? (
                                <CheckCircle size={18} className="text-[#0f6b3a] mt-0.5 flex-shrink-0" weight="fill" />
                              ) : (
                                <span className="mt-0.5 flex-shrink-0">{getCategoryIcon(task.category)}</span>
                              )}
                              <div className="min-w-0 flex-1">
                                <p className={`font-semibold text-sm ${isCompleted ? 'line-through text-[#596065]' : 'text-[#2d3338]'}`}>
                                  {task.title}
                                </p>
                                {task.provider && (
                                  <p className="text-xs text-[#596065] mt-0.5">{task.provider}</p>
                                )}
                                <div className="flex items-center gap-2 mt-2 flex-wrap">
                                  <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${colors.bg} ${colors.text}`}>
                                    {task.category}
                                  </span>
                                  {task.amount != null && (
                                    <span className="text-xs font-bold text-[#2d3338]">
                                      {formatAmount(task.amount, task.currency)}
                                    </span>
                                  )}
                                  {task.recurrence && task.recurrence !== 'none' && (
                                    <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-[#0369a1] bg-[#e0f2fe] px-1.5 py-0.5 rounded">
                                      <ArrowsClockwise size={10} weight="bold" />
                                      {task.recurrence}
                                    </span>
                                  )}
                                  {task.gcal_event_id && (
                                    <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-[#0f6b3a] bg-[#dcfce7] px-1.5 py-0.5 rounded">
                                      <GoogleLogo size={10} weight="bold" />
                                      Synced
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : selectedGcalEvents.length === 0 ? (
                  <div className="surface-card p-6 text-center">
                    <CalendarBlank size={32} className="mx-auto mb-2 text-[#acb3b8]" />
                    <p className="text-sm text-[#596065]">No tasks on this day</p>
                  </div>
                ) : null}

                {/* Google Calendar events */}
                {selectedGcalEvents.length > 0 && (
                  <div>
                    <p className="label-md text-xs text-[#596065] mb-2 flex items-center gap-1">
                      <GoogleLogo size={12} weight="bold" />
                      Google Calendar
                    </p>
                    <div className="space-y-2">
                      {selectedGcalEvents.map(ev => (
                        <div key={ev.id} className="surface-card p-3 border-l-4 border-[#4285f4]">
                          <p className="font-semibold text-sm text-[#2d3338]">{ev.title}</p>
                          {ev.start && ev.start.includes('T') && (
                            <p className="text-xs text-[#596065] mt-0.5">
                              {new Date(ev.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="surface-card p-6 text-center">
                <CalendarBlank size={40} className="mx-auto mb-3 text-[#acb3b8]" />
                <p className="title-md text-base mb-1">Select a date</p>
                <p className="text-sm text-[#596065]">Click on any day to see your tasks and events</p>
              </div>
            )}

            {/* Calendar Stats */}
            <div className="stat-card-dark">
              <p className="label-md text-[#acb3b8] mb-3">This Month</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-2xl font-bold text-white">
                    {tasks.filter(t => {
                      if (!t.due_date) return false;
                      const d = new Date(t.due_date);
                      return d.getMonth() === month && d.getFullYear() === year && t.status === 'pending';
                    }).length}
                  </p>
                  <p className="text-xs text-[#acb3b8]">Pending</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">
                    {tasks.filter(t => {
                      if (!t.due_date) return false;
                      const d = new Date(t.due_date);
                      return d.getMonth() === month && d.getFullYear() === year && t.status === 'completed';
                    }).length}
                  </p>
                  <p className="text-xs text-[#acb3b8]">Completed</p>
                </div>
              </div>
            </div>

            {/* Sync Status */}
            {calendarConnected && (
              <div className="surface-card p-4">
                <div className="flex items-center gap-2 text-[#0f6b3a]">
                  <CheckCircle size={16} weight="fill" />
                  <span className="text-xs font-semibold">Google Calendar Connected</span>
                </div>
              </div>
            )}
            {!checkingCal && !calendarConnected && (
              <div className="surface-card p-4">
                <div className="flex items-center gap-2 text-[#b45309]">
                  <Warning size={16} weight="fill" />
                  <span className="text-xs font-semibold">Calendar not connected</span>
                </div>
                <p className="text-xs text-[#596065] mt-1">
                  Connect Google from the <a href="/connections" className="text-[#005bc1] underline">Connections</a> page to enable sync.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
